import HighchartsMore from 'highcharts/highcharts-more';
import * as Highcharts from 'highcharts/highstock';

import Exporting from 'highcharts/modules/exporting';
import StockTools from 'highcharts/modules/stock-tools';

/**
 * FundChartHighcharts
 *
 * A customized version of the highcharts/highstock module for use in the fundamental chart.
 * It registers the modules the chart depends on and applies custom axis behavior.
 *
 * Note: All fundamental-chart code should import Highcharts from this module instead of directly
 * from 'highcharts' so that Highcharts is consistently configured for fund charts.
 */

// Use exporting module from highcharts
Exporting(Highcharts);
StockTools(Highcharts);
HighchartsMore(Highcharts);

/**
 * Custom Axis extension to allow emulation of negative values on a logarithmic
 * Y axis. Note that the scale is not mathematically correct, as a true
 * logarithmic axis never reaches or crosses zero.
 *
 *  Source: https://www.highcharts.com/blog/snippets/alternative-maths-plotting-negative-values-logarithmic-axis/
 */
Highcharts.addEvent(Highcharts.Axis, 'afterInit', function (this: Highcharts.Axis) {
    const { logarithmic } = this as any;
    const { custom } = this.options as any;

    if (logarithmic && custom && custom.allowNegativeLog) {
        // Avoid errors on negative numbers on a log axis
        (this as any).positiveValuesOnly = false;

        // Override the converter functions
        logarithmic.log2lin = (num: number) => {
            const isNegative = num < 0;

            let adjustedNum = Math.abs(num);

            if (adjustedNum < 10) {
                adjustedNum += (10 - adjustedNum) / 10;
            }

            const result = Math.log(adjustedNum) / Math.LN10;
            return isNegative ? -result : result;
        };

        logarithmic.lin2log = (num: number) => {
            const isNegative = num < 0;

            // eslint-disable-next-line no-restricted-properties
            let result = 10 ** Math.abs(num);
            if (result < 10) {
                result = (10 * (result - 1)) / (10 - 1);
            }
            return isNegative ? -result : result;
        };
    }
});

// By default, if you select a zoom area that is above the data max, it will use the data max as the zoom area max,
// Which relegates the peak of the chart data to the top of the visible chart area, making it difficult to see and annotate that peak.
// Setting allowZoomOutside = true allows you to zoom such that the min or max in the chart is centered in the visible chart area after zooming.
// I haven't found a well-documented way to achieve this. This is sort of a hack based on reading the Highcharts source code:
// https://github.com/highcharts/highcharts/blob/v11.1.0/ts/Core/Axis/Axis.ts#L2695
// @ts-ignore
Highcharts.Axis.prototype.allowZoomOutside = true;

export default Highcharts;
